import { Loader2 } from 'lucide-react'
import VideoLibrary from './VideoLibrary'
import PhotoLibrary from './PhotoLibrary'
import { useState, useEffect } from 'react'
import { CubeLoader } from '@/components/ui/cube-loader'
import { useQuery, useMutation } from '@apollo/client'
import { showNotification } from '@/utils/notifications'
import { Alert, AlertTitle } from '@/components/ui/alert'
import SearchInput from '@/components/common/SearchInput'
import ShowErrors from '@/components/partials/ShowErrors'
import { useMediaBuyingContext } from '@/context/media-buying-context'
import { GET_MEDIA, MIRROR_MEDIA } from '@/graphql/ads/media-library.queries'
import { GET_AD_ASSETS, DELETE_AD_ASSET } from '@/graphql/ads/ad-assets-queries'
import { useWizard } from '@/components/mediaBuying/adCreatives/AdCreativeWizard'

import 'yet-another-react-lightbox/styles.css'

const ERROR_MESSAGE = `We encountered an issue while loading your media files.
    Please try refreshing the page or contact support if the problem persists.`

const EmptyMediaLibrary = ({ onUploadClick }) => (
    <div className="text-center py-5">
        <div className="mb-4">
            <i
                className="fas fa-photo-video"
                style={{
                    fontSize: '64px',
                    color: 'rgba(255, 255, 255, 0.2)',
                    marginBottom: '24px',
                }}
            ></i>
        </div>
        <h4 className="text-muted mb-3">No media files found</h4>
        <p className="text-muted mb-4" style={{ fontSize: '16px', maxWidth: '500px', margin: '0 auto' }}>
            Your media library is empty. Upload photos and videos to your ad account to start seeing it here. Or
        </p>
        <div className="d-flex justify-content-center gap-3">
            <button className="btn btn-outline-primary" onClick={onUploadClick}>
                <i className="fas fa-upload me-2"></i>
                Upload from computer
            </button>
        </div>
    </div>
)

const MediaLibrary = ({
    campaignId,
    adId,
    fetchAdId,
    updateSelectionAsUpload,
    allowMultiple,
    registerNextGuard,
    deregisterNextGuard,
    goToStep,
    markStepAs,
    campaignFormat,
    setStepContentData,
}) => {
    const [selectedPhotos, setSelectedPhotos] = useState([])
    const [selectedVideos, setSelectedVideos] = useState([])
    const [isMirroring, setIsMirroring] = useState(false)
    const { connectedPublishers } = useMediaBuyingContext()
    const publisherId = connectedPublishers ? Object.keys(connectedPublishers)[0] : null
    const adAccountId = publisherId ? connectedPublishers[publisherId].ad_account : null
    const [adAssets, setAdAssets] = useState([])
    const [adAssetsLoading, setAdAssetsLoading] = useState(true)
    const [deleteAdAsset] = useMutation(DELETE_AD_ASSET)
    const [searchQuery, setSearchQuery] = useState('')
    const { getStepContentData, removeStepContentData, cloneCreativeId } = useWizard()
    const [replacingFormat, setReplacingFormat] = useState([])

    useQuery(GET_AD_ASSETS, {
        variables: { campaignId, adId: fetchAdId ?? adId, hasMetaId: true },
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            setAdAssets(data.AdAsset?.adAssets)
            setAdAssetsLoading(false)
        },
    })

    useEffect(() => {
        const replacingFormat = getStepContentData('uploadAssets')?.replaceFormat
        if (replacingFormat) setReplacingFormat(replacingFormat)
    }, [])

    useEffect(() => {
        const ids = adAssets.filter((asset) => asset.source_id && asset.source === 'meta').map((x) => x.source_id)
        setSelectedPhotos(ids)
    }, [adAssets])

    const validate = (selectedButNotUploadedMedia) => {
        if (campaignFormat === 'carousel' && selectedPhotos.length === 0 && selectedVideos.length === 0)
            return 'Please select at least one media file'

        if (campaignFormat === 'video' && selectedVideos.length === 0) return 'Please select a video to continue'
        if (campaignFormat === 'singleImage' && selectedPhotos.length === 0) return 'Please select an image to continue'

        // Don't return error messages for upload states - just prevent progression
        if (isMirroring) return null
        if (selectedButNotUploadedMedia.length > 0) return null

        return null
    }

    useEffect(() => {
        const guard = registerNextGuard(() => {
            const selectedButNotUploadedMedia = [
                ...selectedPhotos.filter((id) => !adAssets.find((a) => a.source_id === id && a.source === 'meta')),
                ...selectedVideos.filter((id) => !adAssets.find((a) => a.source_id === id && a.source === 'meta')),
            ]
            const errorMessage = validate(selectedButNotUploadedMedia)

            // If currently uploading, prevent progression without showing error
            if (isMirroring || selectedButNotUploadedMedia.length > 0) {
                if (selectedPhotos.length > 0 || selectedVideos.length > 0) {
                    startMirroring()
                }

                window.scrollTo({ top: 0, behavior: 'smooth' })

                return false
            }

            if (adAssets.length > 0 && selectedButNotUploadedMedia.length === 0) return true

            if (errorMessage) {
                window.scrollTo({ top: 0, behavior: 'smooth' })
                showNotification(errorMessage, 'error', 3000, null, null, false, true)
                return false
            }

            setStepContentData({ assetId: adAssets[0].id }, calculateNextStep())
            markStepAs(1)

            return true
        })

        return () => deregisterNextGuard(guard)
    }, [selectedPhotos, selectedVideos, adAssets, campaignFormat, isMirroring])

    const { loading, error, data } = useQuery(GET_MEDIA, {
        variables: { adAccountId, publisherId, campaignId },
        skip: !connectedPublishers || !campaignId,
    })

    const calculateNextStep = () => {
        if (campaignFormat === 'carousel') return 'carouselCards'
        else if (campaignFormat === 'video') return 'videoTrim'

        return 'setMediaCrop'
    }

    const goToNextStep = (assetId) => {
        const step = calculateNextStep()
        setStepContentData({ assetId }, step)
        markStepAs(1)
        goToStep(step)
    }

    const [mirrorMedia] = useMutation(MIRROR_MEDIA, {
        onCompleted: (data) => {
            setSelectedPhotos([])
            setSelectedVideos([])
            setIsMirroring(false)
            if (cloneCreativeId) {
                setStepContentData({ cloneWithNewAssets: true }, 'uploadAssets')
            }
            goToNextStep(data.mirrorMedia[0].id)
        },
    })

    const deleteFromDatabase = (metaId) => {
        const asset = adAssets.find((a) => a.source_id === metaId && a.source === 'meta')
        if (asset) {
            deleteAdAsset({ variables: { id: asset.id } })
            setAdAssets((prev) => prev.filter((a) => a.srouce_id !== metaId))
        }
    }

    const handleSelectionToggle = (itemId, setSelection) => {
        setSelection((prev) => {
            if (prev.includes(itemId)) {
                // If already selected, remove it
                deleteFromDatabase(itemId)
                return prev.filter((id) => id !== itemId)
            } else if (allowMultiple) {
                // For carousel, check if the user has selected more than 10 cards
                if (!isAllowedToSelect()) {
                    showNotification('You can only select up to 10 cards', 'error', 3000, null, null, false, true)
                    return prev
                }

                // If multiple selection allowed, add to existing
                return [...prev, itemId]
            } else {
                // If single selection only, replace existing
                return [itemId]
            }
        })
    }

    const isAllowedToSelect = () => {
        if (campaignFormat === 'carousel') {
            const totalSelected = [...selectedPhotos, ...selectedVideos]
            return totalSelected.length < 10
        }

        return true
    }

    const handlePhotoSelectionToggle = (photoId) => handleSelectionToggle(photoId, setSelectedPhotos)
    const handleVideoSelectionToggle = (videoId) => handleSelectionToggle(videoId, setSelectedVideos)

    const photosToMirror = () => {
        return selectedPhotos
            .filter((id) => !adAssets.find((a) => a.source_id === id && a.source === 'meta'))
            .map((id) => {
                const photo = photos.find((p) => p.id === id)
                return { id, type: 'photo', url: photo.url }
            })
    }

    const videosToMirror = () => {
        return selectedVideos
            .filter((id) => !adAssets.find((a) => a.source_id === id && a.source === 'meta'))
            .map((id) => {
                const video = videos.find((v) => v.id === id)
                return { id, type: 'video', url: video.url }
            })
    }

    const startMirroring = async () => {
        const replaceExisting = campaignFormat !== 'carousel' && !replacingFormat.length === 0

        setIsMirroring(true)
        const media = [...photosToMirror(), ...videosToMirror()]

        if (media.length > 0) {
            if (replacingFormat.length) removeStepContentData('uploadAssets', 'replaceFormat')
            await mirrorMedia({
                variables: {
                    media,
                    campaignId,
                    adId,
                    replaceExisting,
                    allowMultiple,
                    formats: replacingFormat,
                },
            })
        }
    }

    if (loading || !data || adAssetsLoading)
        return (
            <div className="tw:w-full tw:flex tw:items-center tw:justify-center">
                <CubeLoader size="default" fullScreen={true} />
            </div>
        )
    if (error) return <ShowErrors errors={[{ message: ERROR_MESSAGE }]} />

    const { photos, videos } = data.Media
    const hasPhotos = photos && photos.length > 0
    const hasVideos = videos && videos.length > 0
    const hasAnyMedia = hasPhotos || hasVideos
    const hasSelectedMedia = selectedPhotos.length > 0 || selectedVideos.length > 0
    const showVideos = campaignFormat === 'video' || (campaignFormat === 'carousel' && hasVideos)
    const showPhotos = (campaignFormat === 'singleImage' || campaignFormat === 'carousel') && hasPhotos

    // If no media at all and no search query, show empty library state
    if (!hasAnyMedia && !searchQuery) {
        return <EmptyMediaLibrary onUploadClick={updateSelectionAsUpload} />
    }

    return (
        <>
            <div className="d-flex align-items-center gap-3 mb-4">
                <SearchInput value={searchQuery} onChange={setSearchQuery} />
                <button
                    className="btn btn-outline-primary px-3 tw:h-9 tw:py-0 tw:inline-flex tw:items-center tw:shrink-0"
                    disabled={!hasSelectedMedia}
                    onClick={() => {
                        setSelectedPhotos([])
                        setSelectedVideos([])
                    }}
                >
                    {allowMultiple ? 'Clear selection' : 'Clear selected'}
                </button>
            </div>

            {isMirroring && (
                <Alert className="tw:mb-8 tw:text-left">
                    <Loader2 className="tw:h-4 tw:w-4 tw:animate-spin" />
                    <AlertTitle className="tw:text-left">Please wait, while we upload your media files</AlertTitle>
                </Alert>
            )}

            <div className="d-flex flex-column" style={{ gap: '48px' }}>
                {showPhotos && (
                    <PhotoLibrary
                        photos={photos}
                        adAssets={adAssets}
                        search={searchQuery}
                        showHeader={showVideos}
                        selectedPhotos={selectedPhotos}
                        handlePhotoSelectionToggle={handlePhotoSelectionToggle}
                    />
                )}

                {showVideos && (
                    <VideoLibrary
                        videos={videos}
                        adAssets={adAssets}
                        search={searchQuery}
                        showHeader={showPhotos}
                        selectedVideos={selectedVideos}
                        handleVideoSelectionToggle={handleVideoSelectionToggle}
                    />
                )}
            </div>
        </>
    )
}

export default MediaLibrary
